import {
  FETCH_DATA_REQUEST,
  FETCH_DATA_SUCCESS,
  FETCH_DATA_FAILURE,
} from "./actionTypes";
import API_ENDPOINTS from "../api/apiEndpoint";
import { axiosBaseQuery, buildUrl, getServiceType } from "../api/axiosBaseQuery";
import { ENCRYPTION } from "../constant/globalConstants";
import { encrypted, tokenManager } from "../utils/Utility";

/**
 * Core fetch action using axiosBaseQuery
 */
export const fetchData = (
  type,
  body = null,
  queryParams = null,
  method = "GET",
  options = {}
) => {
  return async (dispatch) => {
    let endpoint = API_ENDPOINTS[type];
    if (!endpoint) {
      return dispatch({
        type: FETCH_DATA_FAILURE,
        api: type,
        payload: "Invalid API type",
      });
    }

    if (options.urlParams) {
      Object.entries(options.urlParams).forEach(([key, value]) => {
        endpoint = endpoint.replace(`{${key}}`, value);
      });
    }

    dispatch({ type: FETCH_DATA_REQUEST, api: type });

    try {
      const result = await axiosBaseQuery({
        url: endpoint,
        method,
        body,
        params: queryParams,
        options,
      });

      if (result.error) {
        dispatch({
          type: FETCH_DATA_FAILURE,
          api: type,
          payload: result.error.message,
        });
        return result;
      }

      // Success
      let payloadToStore = result.data;
      if (options.responseType === 'blob' || options.responseType === 'arraybuffer') {
        payloadToStore = "Binary data received";
      }
      dispatch({ type: FETCH_DATA_SUCCESS, api: type, payload: payloadToStore });
      return result.data;
    } catch (error) {
      dispatch({
        type: FETCH_DATA_FAILURE,
        api: type,
        payload: error.message,
      });
      return { data: null, error };
    }
  };
};

// GET action creator
export const createGetAction = (apiType, options = {}) => {
  return (queryParams = null) => (dispatch) => {
    return dispatch(fetchData(apiType, null, queryParams, "GET", options));
  };
};

// POST action creator
export const createPostAction = (apiType, options = {}) => {
  return (body = null, queryParams = null) => {
    return (dispatch) => {
      return dispatch(fetchData(apiType, body, queryParams, "POST", options));
    };
  };
};

/**
 * Core streaming fetch action
 */
export const fetchStreamData = (apiType, payload, options, abortController) => {
  let endpoint = API_ENDPOINTS[apiType];

  if (options && options.urlParams) {
    Object.entries(options.urlParams).forEach(([key, value]) => {
      endpoint = endpoint.replace(`{${key}}`, value);
    });
  }

  const serviceType = getServiceType(options);
  const fullUrl = buildUrl(endpoint, serviceType);
  const token = tokenManager.getToken("token");

  const body = ENCRYPTION
    ? JSON.stringify({
      encryptedBody: encrypted.encrypt(JSON.stringify(payload)),
    })
    : JSON.stringify(payload);

  return fetch(fullUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: body,
    signal: abortController?.signal,
  });
};

// ============================================
// API Actions
// ============================================
export const fetchAuthInfo = (queryParams = null) => {
  return createGetAction("authInfo", { skipAuth: true })(queryParams);
};

export const authenticateUser = (body = null) => {
  return createPostAction("authLogin", { skipAuth: true })(body);
};

export const triggerLogout = (queryParams = null) => {
  return createGetAction("authLogout")(queryParams);
};

// Actions
export const fetchCountryDetails = createGetAction("getCountryDetails");
export const fetchProductDetails = createPostAction("getProductDetails");
export const fetchRegiObjectiveDetails = createPostAction("getRegiObjectiveDetails");
export const fetchBinderDetails = createPostAction("getBinderDetails");
export const generateDossierApi = (body) => createPostAction("generateDossier", { responseType: "arraybuffer" })(body);
export const downloadDossierApi = (body) => createPostAction("downloadDossier", { responseType: "blob" })(body);
